import { DisplayEntry, DisplayState, GovernanceEntry, GovernanceSummary } from "./types";

/** Render order for the hover panel: 1d (Daily) first, 1w (Weekly) second, unknown keys last. */
const WINDOW_ORDER: Record<string, number> = { "1d": 0, "1w": 1 };
const windowRank = (window: string): number => (window in WINDOW_ORDER ? WINDOW_ORDER[window] : Number.MAX_SAFE_INTEGER);

/**
 * Picks the entry to surface from the flat `usage` array. The BE owns the
 * actual restriction logic; the FE only decides which window to *show* when
 * several come back:
 *   1. If any entry is `blocked`, pick the one whose `resetsAt` is latest
 *      (that's the constraint that unblocks the user last).
 *   2. Else if any entry is `warning`, pick the one with the highest pctUsed.
 *   3. Else (all `ok`) pick the one with the highest pctUsed.
 *   4. Tie-break on earliest `resetsAt`.
 */
export const pickBindingEntry = (entries: GovernanceEntry[] | undefined): GovernanceEntry | null => {
	if (!entries || entries.length === 0) return null;

	const blocked = entries.filter((e) => e.status === "blocked");
	if (blocked.length > 0) {
		return blocked.reduce((acc, cur) => {
			const a = Date.parse(acc.resetsAt);
			const c = Date.parse(cur.resetsAt);
			if (c > a) return cur;
			if (c < a) return acc;
			return acc;
		});
	}

	const warnings = entries.filter((e) => e.status === "warning");
	const pool = warnings.length > 0 ? warnings : entries;

	return pool.reduce((acc, cur) => {
		if (cur.pctUsed > acc.pctUsed) return cur;
		if (cur.pctUsed < acc.pctUsed) return acc;
		const a = Date.parse(acc.resetsAt);
		const c = Date.parse(cur.resetsAt);
		if (c < a) return cur;
		return acc;
	});
};

/**
 * Maps every `usage` window to a `DisplayEntry` for the hover panel, sorted by
 * window order (1d before 1w, unknown keys last). Returns `[]` when governance
 * is off/bypassed or there are no entries.
 */
export const deriveDisplayEntries = (summary: GovernanceSummary | null | undefined): DisplayEntry[] => {
	if (!summary) return [];
	if (summary.mode === "off" || summary.mode === "bypass") return [];
	if (!summary.usage || summary.usage.length === 0) return [];

	return summary.usage
		.map(
			(e): DisplayEntry => ({
				window: e.window,
				status: e.status,
				pctUsed: e.pctUsed,
				resetsAt: e.resetsAt,
			}),
		)
		.sort((a, b) => windowRank(a.window) - windowRank(b.window));
};

export const deriveDisplayState = (summary: GovernanceSummary | null | undefined): DisplayState => {
	if (!summary) return { kind: "hidden" };
	if (summary.mode === "off" || summary.mode === "bypass") return { kind: "hidden" };

	const entry = pickBindingEntry(summary.usage);
	if (!entry) return { kind: "hidden" };

	if (entry.status === "blocked") {
		return { kind: "blocked", window: entry.window, resetsAt: entry.resetsAt };
	}

	return {
		kind: entry.status,
		window: entry.window,
		pctRemaining: Math.max(0, Math.min(100, 100 - entry.pctUsed)),
		resetsAt: entry.resetsAt,
	};
};
